import React, { useState, useRef, useEffect } from "react";
import { PrimaryButton } from "../../../../globalComponents/ui/Button";
import { SecondaryButton } from "../../../../globalComponents/ui/Button";
import { Modal, ModalHeader } from "../../../../globalComponents/ui/Modal";
import PeriodSelector from "./PeriodSelector";

/* ── Location multi-select dropdown ── */
const LocationMultiSelect = ({ locations, selected = [], onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const toggle = (id) =>
        onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

    const toggleAll = () =>
        onChange(selected.length === locations.length ? [] : locations.map((l) => l.id));

    const allSelected = locations.length > 0 && selected.length === locations.length;

    const label = selected.length === 0
        ? "All Locations"
        : selected.length === locations.length
            ? "All Locations"
            : selected.length === 1
                ? locations.find((l) => l.id === selected[0])?.name || "1 selected"
                : `${selected.length} locations selected`;

    return (
        <div ref={ref} style={{ position: "relative" }}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="kpi-input"
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    textAlign: "left",
                    background: selected.length ? "var(--accent-light, #eff6ff)" : "",
                    borderColor: selected.length ? "var(--accent, #2563eb)" : "",
                    color: selected.length ? "var(--accent, #2563eb)" : "",
                    fontWeight: selected.length ? 600 : 400,
                }}
            >
                <span>{label}</span>
                <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 8 }}>{open ? "▲" : "▾"}</span>
            </button>

            {/* Dropdown */}
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    zIndex: 999, background: "#fff",
                    border: "1px solid var(--rule, #e2e8f0)",
                    borderRadius: 10,
                    boxShadow: "0 12px 32px rgba(0,0,0,.13)",
                    maxHeight: 220, overflowY: "auto",
                    padding: "4px 0",
                }}>
                    {/* Select all */}
                    <label style={{
                        display: "flex", gap: 8, padding: "8px 13px", cursor: "pointer",
                        fontSize: 12, fontWeight: 700,
                        borderBottom: "1px solid var(--rule, #e2e8f0)",
                        background: allSelected ? "var(--accent-light, #eff6ff)" : "transparent",
                        alignItems: "center",
                    }}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }}
                        />
                        All Locations
                    </label>

                    {locations.map((loc) => {
                        const checked = selected.includes(loc.id);
                        return (
                            <label key={loc.id} style={{
                                display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer",
                                fontSize: 12,
                                background: checked ? "var(--accent-light, #eff6ff)" : "transparent",
                                alignItems: "center",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(loc.id)}
                                    style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }}
                                />
                                <span style={{ fontWeight: 600, color: "var(--ink-2, #334155)" }}>{loc.name}</span>
                            </label>
                        );
                    })}
                </div>
            )}


            {selected.length > 0 && selected.length < locations.length && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                    {selected.map((id) => {
                        const loc = locations.find((l) => l.id === id);
                        return (
                            <span key={id} style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "2px 8px 2px 10px", borderRadius: 20,
                                background: "var(--accent-light, #eff6ff)",
                                border: "1px solid var(--accent-border, #bfdbfe)",
                                fontSize: 11.5, fontWeight: 600,
                                color: "var(--accent, #2563eb)",
                            }}>
                                {loc?.name || `#${id}`}
                                <button
                                    onClick={() => toggle(id)}
                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: "#93c5fd", fontSize: 14, lineHeight: 1, padding: 0 }}
                                >×</button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ── Generic people multi-select (assigned-to / assigned-by / auditor) ── */
const PeopleMultiSelect = ({ options = [], selected = [], onChange, allLabel = "All", nounPlural = "selected" }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const toggle = (id) =>
        onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    const toggleAll = () =>
        onChange(selected.length === options.length ? [] : options.map((o) => o.id));

    const allSelected = options.length > 0 && selected.length === options.length;
    const label = selected.length === 0 || allSelected
        ? allLabel
        : selected.length === 1
            ? options.find((o) => o.id === selected[0])?.name || `1 ${nounPlural}`
            : `${selected.length} ${nounPlural}`;

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="kpi-input"
                style={{
                    width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    cursor: "pointer", textAlign: "left",
                    background: selected.length ? "var(--accent-light, #eff6ff)" : "",
                    borderColor: selected.length ? "var(--accent, #2563eb)" : "",
                    color: selected.length ? "var(--accent, #2563eb)" : "",
                    fontWeight: selected.length ? 600 : 400,
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 8 }}>{open ? "▲" : "▾"}</span>
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    zIndex: 999, background: "#fff", border: "1px solid var(--rule, #e2e8f0)",
                    borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.13)",
                    maxHeight: 220, overflowY: "auto", padding: "4px 0",
                }}>
                    {options.length === 0 ? (
                        <div style={{ padding: "10px 13px", fontSize: 12, color: "var(--ink-4)" }}>No options</div>
                    ) : (
                        <>
                            <label style={{
                                display: "flex", gap: 8, padding: "8px 13px", cursor: "pointer",
                                fontSize: 12, fontWeight: 700, borderBottom: "1px solid var(--rule, #e2e8f0)",
                                background: allSelected ? "var(--accent-light, #eff6ff)" : "transparent", alignItems: "center",
                            }}>
                                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                                    style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }} />
                                {allLabel}
                            </label>
                            {options.map((opt) => {
                                const checked = selected.includes(opt.id);
                                return (
                                    <label key={opt.id} style={{
                                        display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer", fontSize: 12,
                                        background: checked ? "var(--accent-light, #eff6ff)" : "transparent", alignItems: "center",
                                    }}>
                                        <input type="checkbox" checked={checked} onChange={() => toggle(opt.id)}
                                            style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }} />
                                        <span style={{ fontWeight: 600, color: "var(--ink-2, #334155)" }}>{opt.name}</span>
                                    </label>
                                );
                            })}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* ── Auditor filter: cascading Type → Levels (multi) → Auditors ──
   The auditor list is scoped to the chosen type and (if any) selected levels. */
const AUDITOR_TYPE_LABELS = { INTERNAL: "Internal", EXTERNAL: "External" };

const AuditorFilter = ({ auditorsByType = {}, value = {}, onChange }) => {
    const toStr = (v) => String(v ?? "");

    const type = value.type || "";
    const levels = (value.levels || []).map(toStr);
    const auditorIds = (value.auditorIds || []).map(toStr);

    const types = Object.keys(auditorsByType).filter((t) => (auditorsByType[t] || []).length);
    const typeAuditors = auditorsByType[type] || [];

    // Levels available for the chosen type (union across its auditors).
    const levelSet = new Set();
    typeAuditors.forEach((a) => (a.levels || []).forEach((l) => levelSet.add(toStr(l))));
    const levelOptions = [...levelSet].sort((x, y) => Number(x) - Number(y)).map((l) => ({ id: l, name: `Level ${l}` }));

    // Auditors of the chosen type, narrowed to those on the selected levels.
    const auditorOptions = typeAuditors
        .filter((a) => levels.length === 0 || (a.levels || []).some((l) => levels.includes(toStr(l))))
        .map((a) => ({ id: toStr(a.userId ?? a.id), name: a.name }));

    const setType = (t) => onChange({ type: t, levels: [], auditorIds: [] });
    const setLevels = (ids) => {
        const strLevels = ids.map(toStr);
        // Drop any picked auditors that no longer fall under the new level set.
        const allowed = new Set(
            typeAuditors
                .filter((a) => strLevels.length === 0 || (a.levels || []).some((l) => strLevels.includes(toStr(l))))
                .map((a) => toStr(a.userId ?? a.id))
        );
        onChange({ type, levels: strLevels, auditorIds: auditorIds.filter((id) => allowed.has(id)) });
    };
    const setAuditors = (ids) => onChange({ type, levels, auditorIds: ids.map(toStr) });

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Step 1: auditor type */}
            <div>
                <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 5 }}>Auditor type</div>
                <select
                    className="kpi-input"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    style={{ width: "100%", cursor: "pointer" }}
                >
                    <option value="">Select auditor type…</option>
                    {types.map((t) => <option key={t} value={t}>{AUDITOR_TYPE_LABELS[t] || t}</option>)}
                </select>
            </div>

            {/* Step 2: levels (multi) */}
            {type && (
                <div>
                    <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 5 }}>Levels</div>
                    <PeopleMultiSelect
                        options={levelOptions}
                        selected={levels.map(String)}
                        onChange={setLevels}
                        allLabel="All Levels"
                        nounPlural="levels"
                    />
                </div>
            )}

            {/* Step 3: auditors (scoped to type + levels) */}
            {type && (
                <div>
                    <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 5 }}>Auditors</div>
                    <PeopleMultiSelect
                        options={auditorOptions}
                        selected={auditorIds}
                        onChange={setAuditors}
                        allLabel="All Auditors"
                        nounPlural="auditors"
                    />
                </div>
            )}
        </div>
    );
};

/* ── Main modal ── */
const KpiFiltersModal = ({
    show,
    onHide,
    filters = {},
    onApply,
    onReset,
    locations = [],
    assignedToOptions = [],
    assignedByOptions = [],
    auditorsByType = {},
    auditorsError = false,
    isLoading,
    // ── Period selector ──────────────────────────────────
    fyLabel = "",
    startingMonth = 1,
    periodSelectorState = { periodType: "", selectedSlot: null, customFrom: "", customTo: "" },
}) => {
    const [localFilters, setLocalFilters] = useState(filters);
    const [localPeriodState, setLocalPeriodState] = useState(periodSelectorState);

    // Sync from props when modal is opened
    useEffect(() => {
        if (show) {
            setLocalFilters(filters);
            setLocalPeriodState(periodSelectorState);
        }
    }, [show, filters, periodSelectorState]);

    // Keep localFilters date range in sync with localPeriodState
    useEffect(() => {
        const { periodType, selectedSlot, customFrom, customTo } = localPeriodState;
        let from = "";
        let to = "";
        if (periodType === "CUSTOM") {
            from = customFrom;
            to = customTo;
        } else if (selectedSlot) {
            from = selectedSlot.fromDate;
            to = selectedSlot.toDate;
        }
        setLocalFilters(prev => {
            if (prev.from_date === from && prev.to_date === to) return prev;
            return { ...prev, from_date: from, to_date: to };
        });
    }, [localPeriodState]);

    const activeCount = Object.entries(localFilters).filter(([k, v]) => {
        if (k === "auditorFilter") return Boolean(v?.type);
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
    }).length;

    const handle = (key, value) => {
        setLocalFilters(prev => ({ ...prev, [key]: value }));
    };

    const Section = ({ label, children }) => (
        <div>
            <label className="kpi-label" style={{ marginBottom: 10 }}>{label}</label>
            {children}
        </div>
    );

    const PillGroup = ({ options, active, onSelect }) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {options.map(({ value, label, icon }) => (
                <button
                    key={value}
                    onClick={() => onSelect(active === value ? "" : value)}
                    className={`kpi-filter-pill${active === value ? " active" : ""}`}
                >
                    {icon && <span>{icon} </span>}
                    {label}
                </button>
            ))}
        </div>
    );

    return (
        <Modal show={show} onClose={onHide} maxWidth={520}>
            <ModalHeader
                emoji="🎛️"
                title={
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        Advanced Filters
                        {activeCount > 0 && (
                            <span style={{
                                padding: "2px 8px",
                                background: "var(--accent)",
                                color: "#fff",
                                borderRadius: 20,
                                fontSize: 10,
                                fontWeight: 700,
                             }}>
                                {activeCount} active
                            </span>
                        )}
                    </span>
                }
                onClose={onHide}
            />

            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 22, overflowY: "auto" }}>

                {/* ── Locations ── */}
                {locations.length > 0 && (
                    <Section label="Location (Unit Code)">
                        <LocationMultiSelect
                            locations={locations}
                            selected={localFilters.location_ids || []}
                            onChange={(v) => handle("location_ids", v)}
                        />
                    </Section>
                )}

                {/* ── Status ── */}
                <Section label="Status">
                    <PillGroup
                        active={localFilters.status || ""}
                        onSelect={(v) => handle("status", v)}
                        options={[
                            { value: "", label: "All" },
                            { value: "pending", label: "Pending", icon: "⏳" },
                            { value: "completed", label: "Completed", icon: "✅" },
                        ]}
                    />
                </Section>

                {/* ── Assigned To ── */}
                {assignedToOptions.length > 0 && (
                    <Section label="Assigned To">
                        <PeopleMultiSelect
                            options={assignedToOptions}
                            selected={localFilters.assigned_to_ids || []}
                            onChange={(v) => handle("assigned_to_ids", v)}
                            allLabel="All Assignees"
                            nounPlural="assignees"
                        />
                    </Section>
                )}

                {/* ── Assigned By ── */}
                {assignedByOptions.length > 0 && (
                    <Section label="Assigned By">
                        <PeopleMultiSelect
                            options={assignedByOptions}
                            selected={localFilters.assigned_by_ids || []}
                            onChange={(v) => handle("assigned_by_ids", v)}
                            allLabel="All Assigners"
                            nounPlural="assigners"
                        />
                    </Section>
                )}

                {/* ── Auditors: type → levels → auditors ── */}
                {(auditorsError || Object.values(auditorsByType).some((l) => (l || []).length > 0)) && (
                    <Section label="Auditors">
                        {auditorsError ? (
                            <div style={{ fontSize: 11, color: "var(--amber-700, #b45309)", fontStyle: "italic", padding: "4px 0" }}>
                                Auditor data unavailable (service degraded)
                            </div>
                        ) : (
                            <AuditorFilter
                                auditorsByType={auditorsByType}
                                value={localFilters.auditorFilter || { type: "", levels: [], auditorIds: [] }}
                                onChange={(v) => handle("auditorFilter", v)}
                            />
                        )}
                    </Section>
                )}

                {/* ── Date Range ── */}
                {fyLabel && (
                    <Section label="Date Range">
                        <PeriodSelector
                            fyLabel={fyLabel}
                            startingMonth={startingMonth}
                            periodType={localPeriodState.periodType}
                            selectedSlot={localPeriodState.selectedSlot}
                            customFrom={localPeriodState.customFrom}
                            customTo={localPeriodState.customTo}
                            onPeriodTypeChange={(type) => setLocalPeriodState(prev => ({ ...prev, periodType: type, selectedSlot: null }))}
                            onSlotChange={(slot) => setLocalPeriodState(prev => ({ ...prev, selectedSlot: slot }))}
                            onCustomFromChange={(val) => setLocalPeriodState(prev => ({ ...prev, customFrom: val }))}
                            onCustomToChange={(val) => setLocalPeriodState(prev => ({ ...prev, customTo: val }))}
                        />
                        {(localPeriodState.selectedSlot || (localPeriodState.periodType === "CUSTOM" && (localPeriodState.customFrom || localPeriodState.customTo))) && (
                            <div style={{ marginTop: 8, fontSize: 11, color: "var(--accent, #2563eb)", fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                                {localPeriodState.selectedSlot
                                    ? `${localPeriodState.selectedSlot.fromDate} → ${localPeriodState.selectedSlot.toDate}`
                                    : `${localPeriodState.customFrom || "…"} → ${localPeriodState.customTo || "…"}`}
                            </div>
                        )}
                    </Section>
                )}
                {!fyLabel && (
                    <Section label="Date Range">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                                <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>From</div>
                                <input type="date" className="kpi-input" value={localFilters.from_date || ""} onChange={(e) => handle("from_date", e.target.value)} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>To</div>
                                <input type="date" className="kpi-input" value={localFilters.to_date || ""} onChange={(e) => handle("to_date", e.target.value)} />
                            </div>
                        </div>
                    </Section>
                )}

                {/* ── Footer ── */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 16,
                    borderTop: "1px solid var(--rule)",
                }}>
                    <SecondaryButton
                        onClick={() => { onReset?.(); onHide(); }}
                    >
                        Reset All
                    </SecondaryButton>
                    <div style={{ display: "flex", gap: 8 }}>
                        <SecondaryButton onClick={onHide}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            type="button"
                            disabled={isLoading}
                            onClick={() => {
                                onApply?.(localFilters, localPeriodState);
                                onHide();
                            }}
                        >
                            {isLoading ? "Loading..." : "Apply Filters"}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default KpiFiltersModal;